import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

/**
 * Validates whether a phone number is valid for a given country.
 * Accepts numbers with or without country prefix (e.g. "+91 98765 43210" or "098765 43210").
 *
 * @param {string | null | undefined} phone - The phone number string to validate.
 * @param {string} [country='IN'] - ISO 3166-1 alpha-2 country code (e.g. 'IN', 'US', 'GB').
 * @returns {boolean} True if valid, false if invalid or empty.
 */
export const validatePhoneNumber = (phone, country = "IN") => {
  if (!phone) return false;
  try {
    return isValidPhoneNumber(phone, country);
  } catch {
    return false;
  }
};

/**
 * Formats a phone number into international format (e.g. "+91 98765 43210").
 * Falls back to the original input string if parsing fails.
 *
 * @param {string | null | undefined} phone - The phone number string to format.
 * @param {string} [country='IN'] - ISO 3166-1 alpha-2 country code.
 * @returns {string} Formatted international phone number, or original value if invalid.
 */
export const formatPhoneNumber = (phone, country = "IN") => {
  if (!phone) return "—";
  try {
    return parsePhoneNumber(phone, country).formatInternational();
  } catch {
    return phone;
  }
};

/**
 * Returns metadata for a phone number — its detected country and validity.
 *
 * @param {string | null | undefined} phone - The phone number string to inspect.
 * @param {string} [country='IN'] - ISO 3166-1 alpha-2 country code.
 * @returns {{ country: string, isValid: boolean } | null} Metadata object, or null if parsing fails.
 */
export const getPhoneMetadata = (phone, country = "IN") => {
  if (!phone) return null;
  try {
    const parsed = parsePhoneNumber(phone, country);
    return { country: parsed.country, isValid: parsed.isValid() };
  } catch {
    return null;
  }
};

export const isStrictValidPhone = (phone) => {
  if (!phone) return false;
  try {
    const cleaned = phone.replace(/[\s\-().]/g, "");

    // Has + prefix — validate as international
    if (cleaned.startsWith("+")) {
      const parsed = parsePhoneNumber(cleaned);
      return parsed.isValid();
    }

    // Treat a leading country code without '+' as international before trying
    // the local-number fallbacks. A leading 0 is a local trunk prefix, not a
    // country code (for example, UK 07911...).
    if (!cleaned.startsWith("0")) {
      const international = parsePhoneNumber(`+${cleaned}`);
      if (international.isValid()) return true;
    }

    // No country code match — pass the original value to fallback countries.
    const fallbackCountries = ["IN", "GB", "US"];
    return fallbackCountries.some((country) => {
      try {
        return isValidPhoneNumber(cleaned, country);
      } catch {
        return false;
      }
    });
  } catch {
    return false;
  }
};
